/**
 * CountryCombobox — searchable country picker.
 *
 * Feb 2026 user request: *"why is the list of countries not in
 * alphabetical order in register. also make it serachable."*
 *
 * Uses a lightweight custom implementation (input + filtered list +
 * click-outside close) so we don't need to pull in cmdk/Popover
 * plumbing just for one selector. Preserves the exact same public
 * contract as the previous native <select>: controlled `value`
 * (ISO alpha-2 code) + `onChange(code)` handler.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Globe, ChevronDown, Check, Search } from 'lucide-react';
import { COUNTRIES } from '../utils/countries';

const CountryCombobox = ({
  value,
  onChange,
  placeholder = 'Country (optional)',
  className = '',
  testId = 'country-combobox',
  inputPaddingClass = 'py-[19px]',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  const selected = useMemo(
    () => COUNTRIES.find((c) => c.code === value) || null,
    [value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().startsWith(q),
    );
  }, [query]);

  // Close when clicking outside the container.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (open && searchInputRef.current) {
      // Give the dropdown a tick to render before focusing.
      const t = setTimeout(() => searchInputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  const pick = (code) => {
    onChange?.(code);
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={containerRef} className={`relative ${className}`} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full pl-12 pr-10 ${inputPaddingClass} rounded-md bg-slate-900/50 border border-slate-700 text-left text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 flex items-center justify-between`}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={`${testId}-trigger`}
      >
        <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
        <span className={`truncate ${selected ? 'text-white' : 'text-gray-500'}`}>
          {selected ? `${selected.flag} ${selected.name}` : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute left-0 right-0 top-full mt-1 z-50 rounded-md bg-slate-900 border border-slate-700 shadow-2xl overflow-hidden"
          data-testid={`${testId}-panel`}
          role="listbox"
        >
          <div className="relative border-b border-slate-800">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              ref={searchInputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search country…"
              className="w-full pl-9 pr-3 py-2.5 bg-slate-900 text-white text-sm focus:outline-none"
              data-testid={`${testId}-search`}
              autoComplete="off"
            />
          </div>
          <ul className="max-h-64 overflow-y-auto">
            {filtered.length === 0 ? (
              <li className="px-3 py-3 text-sm text-slate-500" data-testid={`${testId}-empty`}>
                No countries match "{query}".
              </li>
            ) : (
              filtered.map((c) => {
                const isSelected = c.code === value;
                return (
                  <li key={c.code}>
                    <button
                      type="button"
                      onClick={() => pick(c.code)}
                      className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left transition-colors ${isSelected ? 'bg-indigo-600/30 text-white' : 'text-slate-200 hover:bg-slate-800'}`}
                      data-testid={`${testId}-opt-${c.code}`}
                      role="option"
                      aria-selected={isSelected}
                    >
                      <span className="flex items-center gap-2 truncate">
                        <span className="text-base leading-none">{c.flag}</span>
                        <span className="truncate">{c.name}</span>
                        <span className="text-[10px] text-slate-500 font-mono">{c.code}</span>
                      </span>
                      {isSelected && <Check className="w-4 h-4 text-indigo-300 flex-shrink-0" />}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CountryCombobox;
